import { attr, css, FASTElement, html, nullableNumberConverter, observable, Observable } from '@microsoft/fast-element';
import { Swatch } from '../../color';
import { swatchConverter } from '../../value-converters/swatch-value-converter';
import { stringListValueConverter } from '../../value-converters/string-list-value-converter';
import { backgroundStyles, fontStyles } from './design-system-provider.styles';
import { fillColor, overrideDesignTokensFromJson, designTokens, brand } from '../../design-tokens';
import { typeRampStyles } from '../../styles/typography';
import { DesignTokenValue } from '../../foundation/design-token-node';
import { NatGenDesignSystem } from '../../natgen-design-system';
import { display } from '../../foundation/display';
import { CSSDesignToken } from '../../foundation/design-token';

import defaultTheme from '../../design-tokens/default.tokens.json';

// TODO: Load at runtime?

// Brands
import ngicTheme from '../../design-tokens/brands/ngic.tokens.json';
import allstateTheme from '../../design-tokens/brands/allstate.tokens.json';
import daTheme from '../../design-tokens/brands/directauto.tokens.json';
import encompassTheme from '../../design-tokens/brands/encompass.tokens.json';
import fuiTheme from '../../design-tokens/brands/fui.tokens.json';
import goodsamTheme from '../../design-tokens/brands/goodsam.tokens.json';
import natgenpremierTheme from '../../design-tokens/brands/natgenpremier.tokens.json';
import terminalTheme from '../../design-tokens/brands/terminal.tokens.json';

// Variants
import salesVariant from '../../design-tokens/variants/sales.tokens.json';
import { backgroundColorContext } from '../../context/background-context';
import { isDark } from '../../utilities/is-dark';
import { miniComponents } from '../../styles/mini-components';

function designToken<T>(token: CSSDesignToken<T>) {
    return (source: NatGenDesignSystemProvider, key: string) => {
        source[key + 'Changed'] = function (
            this: NatGenDesignSystemProvider,
            prev: T | undefined,
            next: T | undefined
        ) {
            if (next !== undefined && next !== null) {
                token.setValueFor(this, next as DesignTokenValue<T>);
            } else {
                token.deleteValueFor(this);
            }
        };
    };
}

// TODO: Do we need these enumerated on this provider anymore?
function designTokenKey<T>(tokenKey: string) {
    return (source: NatGenDesignSystemProvider, key: string) => {
        source[key + 'Changed'] = function (
            this: NatGenDesignSystemProvider,
            prev: T | undefined,
            next: T | undefined
        ) {
            if (next !== undefined && next !== null) {
                designTokens[tokenKey].setValueFor(this, next as DesignTokenValue<T>);
            } else {
                designTokens[tokenKey].deleteValueFor(this);
            }
        };
    };
}

/**
 * @element natgen-design-system-provider
 */
export class NatGenDesignSystemProvider extends FASTElement {
    isRoot: boolean = false;

    constructor() {
        super();

        // If fillColor or baseLayerLuminance change, we need to
        // re-evaluate whether we should have paint styles applied
        const subscriber = {
            handleChange: this.noPaintChanged.bind(this),
        };

        Observable.getNotifier(this).subscribe(subscriber, 'fillColor');
        Observable.getNotifier(this).subscribe(subscriber, 'baseLayerLuminance');
    }

    connectedCallback(): void {
        super.connectedCallback();

        this.isRoot = this.parentElement.closest('natgen-design-system-provider') == null;

        if (this.isRoot) {
            typeRampStyles.addStylesTo(document);
            miniComponents.addStylesTo(document);
        }
    }

    disconnectedCallback(): void {
        if (this.isRoot) {
            miniComponents.removeStylesFrom(document);
            typeRampStyles.removeStylesFrom(document);
        }
    }

    /**
     * Used to instruct the NatGenDesignSystemProvider
     * that it should not set the CSS
     * background-color and color properties
     *
     * @remarks
     * HTML boolean attribute: no-paint
     */
    @attr({ attribute: 'no-paint', mode: 'boolean' })
    public noPaint = false;
    private noPaintChanged() {
        if (!this.noPaint) {
            backgroundColorContext.provide(this, isDark(this.fillColor ?? designTokens['color-fill-color'].getValueFor(this)));
            this.$fastController.addStyles(backgroundStyles);
        } else {
            this.$fastController.removeStyles(backgroundStyles);
        }
    }

    @attr({ attribute: "use-default-typography", mode: "boolean" })
    public useDefaultTypography: boolean = false;
    private useDefaultTypographyChanged() {
        if (this.useDefaultTypography) {
            this.$fastController.addStyles(fontStyles);
        } else {
            this.$fastController.removeStyles(fontStyles);
        }
    }

    @attr
    public brand: string = "ngic";
    private brandChanged(prev: string, next: string) {
        if (prev !== next)
            this.onTokensChanged();
    }

    @attr({
        converter: stringListValueConverter
    })
    public variants: string[];
    private variantsChanged(prev: string, next: string) {
        if (prev !== next)
            this.onTokensChanged();
    }

    private onTokensChanged() {
        Object.keys(designTokens).forEach(k => designTokens[k].deleteValueFor(this));

        switch(this.brand)
        {
            case 'ngic':
                overrideDesignTokensFromJson(ngicTheme, this);
                break;
            case 'allstate':
                overrideDesignTokensFromJson(allstateTheme, this);
                break;
            case 'directauto':
                overrideDesignTokensFromJson(daTheme, this)
                break;
            case 'encompass':
                overrideDesignTokensFromJson(encompassTheme, this)
                break;
            case 'goodsam':
                overrideDesignTokensFromJson(goodsamTheme, this)
                break;
            case 'fui':
                overrideDesignTokensFromJson(fuiTheme, this);
                break;
            case 'natgenpremier':
                overrideDesignTokensFromJson(natgenpremierTheme, this);
                break;
            case 'terminal':
                overrideDesignTokensFromJson(terminalTheme, this);
                break;
            default:
                overrideDesignTokensFromJson(defaultTheme, this);
                break;
        }

        // TODO: Loop when we have more than one variant:
        if (this.variants?.includes('sales'))
            overrideDesignTokensFromJson(salesVariant, this);

        if (brand.getValueFor(this) != this.brand) {
            brand.setValueFor(this, this.brand);
            this.noPaintChanged();
        }
    }

    /**
     * The {@link https://www.w3.org/WAI/GL/wiki/Relative_luminance#:~:text=WCAG%20definition%20of%20relative%20luminance,and%201%20for%20lightest%20white|relative luminance} of the base layer of the application.
     *
     * @remarks
     * When set to a number between 0 and 1, this value controls the luminosity of the neutral layers.
     *
     * HTML attribute: base-layer-luminance
     *
     * CSS custom property: N/A
     */
    @attr({
        attribute: "base-layer-luminance",
        converter: nullableNumberConverter,
    })
    @designTokenKey('color-base-layer-luminance')
    public baseLayerLuminance: number;

    /**
     * Define design system property attributes
     * @remarks
     * HTML attribute: background-color
     *
     * CSS custom property: --fill-color
     */
    @attr({
        attribute: 'background-color',
        converter: swatchConverter,
    })
    @designToken(fillColor)
    public fillColor: Swatch;

    /**
     * This color is used to grab the user's attention.
     */
    @attr({
        attribute: 'error-color',
        converter: swatchConverter,
    })
    @designTokenKey('color-error')
    public errorColor: string;

    /**
     * This color is used to grab the user's attention.
     */
    @attr({
        attribute: 'success-color',
        converter: swatchConverter,
    })
    @designTokenKey('color-success')
    public successColor: string;

    /**
     *
     * The density offset, used with designUnit to calculate height and spacing.
     *
     * @remarks
     * HTML attribute: density
     *
     * CSS custom property: --density
     */
    @attr({
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-density')
    public density: number;

    /**
     * The grid-unit that UI dimensions are derived from in pixels.
     *
     * @remarks
     * HTML attribute: design-unit
     *
     * CSS custom property: --space-design-unit
     */
    @attr({
        attribute: "design-unit",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-design-unit')
    public designUnit: number;

    /**
     * The number of designUnits used for component height at the base density.
     *
     * @remarks
     * HTML attribute: base-height-units
     *
     * CSS custom property: --base-height-units
     */
    @attr({
        attribute: "base-height-units",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-base-height-units')
    public baseHeightMultiplier: number;

    /**
     * The number of designUnits used for horizontal spacing at the base density.
     *
     * @remarks
     * HTML attribute: base-horizontal-spacing-units
     *
     * CSS custom property: --base-horizontal-spacing-units
     */
    @attr({
        attribute: "base-horizontal-spacing-units",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-base-horizontal-spacing-units')
    public baseHorizontalSpacingMultiplier: number;

    /**
     * The corner radius applied to controls.
     *
     * @remarks
     * HTML attribute: corner-radius
     *
     * CSS custom property: --corner-radius
     */
    @attr({
        attribute: "corner-radius",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-corner-radius')
    public cornerRadius: number;

    /**
     * The corner radius applied to inputs.
     *
     * @remarks
     * HTML attribute: input-corner-radius
     *
     * CSS custom property: --input-corner-radius
     */
    @attr({
        attribute: "input-corner-radius",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-input-corner-radius')
    public inputCornerRadius: number;

    /**
     * The corner radius applied to buttons.
     *
     * @remarks
     * HTML attribute: button-corner-radius
     *
     * CSS custom property: --button-corner-radius
     */
    @attr({
        attribute: "button-corner-radius",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-button-corner-radius')
    public buttonCornerRadius: number;

    /**
     * The width of the standard stroke applied to stroke components in pixels.
     *
     * @remarks
     * HTML attribute: stroke-width
     *
     * CSS custom property: --stroke-width
     */
    @attr({
        attribute: "stroke-width",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-stroke-width')
    public strokeWidth: number;

    /**
     * The width of the standard focus stroke in pixels.
     *
     * @remarks
     * HTML attribute: focus-stroke-width
     *
     * CSS custom property: --focus-stroke-width
     */
    @attr({
        attribute: "focus-stroke-width",
        converter: nullableNumberConverter,
    })
    @designTokenKey('space-focus-stroke-width')
    public focusStrokeWidth: number;

    /**
     * The opacity of a disabled control.
     *
     * @remarks
     * HTML attribute: disabled-opacity
     *
     * CSS custom property: --disabled-opacity
     */
    @attr({
        attribute: "disabled-opacity",
        converter: nullableNumberConverter,
    })
    @designTokenKey('color-disabled-opacity')
    public disabledOpacity: number;
}

/**
 * A function that returns a {@link DesignSystemProvider} registration for configuring the component with a DesignSystem.
 * @public
 * @remarks
 * Generates HTML Element: `<natgen-design-system-provider>`
 */
export const natGenDesignSystemProviderDefinition = NatGenDesignSystemProvider.compose({
    name: `${NatGenDesignSystem.prefix}-design-system-provider`,
    template: html`<slot></slot>`,
    styles: css`${display('block')}`
});
